import { cache } from "react";
import { LogUtils } from "./log-utils";
import { fetchCsrfTokenServer, type ServerCsrfTokenResponse } from "./server-csrf";

interface FetchDataServerRequest {
  endpoint: string;
  cache?: RequestCache;
  body?: unknown;
  headers?: Record<string, string>;
  tags?: string[];
  revalidate?: number;
}

const getCachedServerCsrfToken = cache(async (): Promise<ServerCsrfTokenResponse> => {
  return fetchCsrfTokenServer();
});

async function getServerCsrfToken(forceRefresh = false): Promise<ServerCsrfTokenResponse> {
  if (forceRefresh) {
    return fetchCsrfTokenServer();
  }

  return getCachedServerCsrfToken();
}

function buildHeaders(
  cookies: string | null,
  customHeaders?: Record<string, string>
): Record<string, string> {
  return {
    "content-type": "application/json",
    Accept: "application/json",
    ...(cookies ? { Cookie: cookies } : {}),
    ...(customHeaders || {}),
  };
}

function buildRequestBody(body: unknown, csrfToken: string) {
  const requestBody: Record<string, unknown> =
    body && typeof body === "object" ? { ...(body as Record<string, unknown>) } : {};
  requestBody._csrf = csrfToken;
  return requestBody;
}

export const fetchDataServer = async (request: FetchDataServerRequest) => {
  try {
    const {
      endpoint,
      body,
      cache = "no-store",
      tags,
      revalidate,
      headers: customHeaders,
    } = request;

    let triedRefresh = false;
    let response: Response;

    do {
      const { token, cookies } = await getServerCsrfToken(triedRefresh);
      if (!token) {
        LogUtils.error(`Server CSRF token unavailable for: ${endpoint}`);
        return null;
      }

      response = await fetch(endpoint, {
        method: "POST",
        headers: buildHeaders(cookies, customHeaders),
        body: JSON.stringify(buildRequestBody(body, token)),
        cache,
        ...(tags && { next: { tags } }),
        ...(revalidate && { next: { revalidate } }),
      });

      if (
        !response.ok &&
        (response.status === 401 || response.status === 404 || response.status === 403) &&
        !triedRefresh
      ) {
        triedRefresh = true;
        continue;
      }
      break;
    } while (true);

    const contentType = response?.headers?.get("content-type");
    const data = contentType?.includes("application/json")
      ? await response.json()
      : await response.text();

    if (response.ok) {
      return data;
    }

    return null;
  } catch (error: unknown) {
    LogUtils.error(`Error fetching data from : ${request.endpoint}`, error);
    return null;
  }
};
